import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, DollarSign, Loader2, LogOut, ShieldCheck, Sparkles, Tag, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { uploadChunkedFile } from "@/lib/upload";
import { VideosTab } from "@/components/admin/VideosTab";
import { VideographyTab } from "@/components/admin/VideographyTab";
import { ServicesTab } from "@/components/admin/ServicesTab";

const TABS = ["Overview", "Beats", "Offers", "Orders", "Bookings", "Discounts", "Videos", "Galleries", "Videography", "Services", "Catalog", "Socials", "Messages"];
const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";

const Stat = ({ label, value, id }) => (
  <div className="rounded-2xl border border-white/10 bg-[#141419] p-5" data-testid={`stat-${id}`}>
    <p className="font-heading text-3xl font-extrabold text-mint-gradient">{value}</p>
    <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{label}</p>
  </div>
);

import { GENRES } from "@/lib/genres";

const EMPTY_BEAT = { title: "", genre: "Trap", bpm: 140, musical_key: "", tags: "", free_download: false, price_mp3: 17.99, price_wav: 27.99, price_exclusive: 174.99 };

function BeatsTab() {
  const [beats, setBeats] = useState([]);
  const [form, setForm] = useState(EMPTY_BEAT);
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [stems, setStems] = useState(null);
  const [aiCoverBusy, setAiCoverBusy] = useState(null);
  const [uploadProgress, setUploadProgress] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [tagChecking, setTagChecking] = useState(false);

  const load = () => api.get("/admin/beats").then((r) => setBeats(r.data.filter((b) => b.is_active)));
  useEffect(() => { load().catch(() => {}); }, []);

  const aiAssist = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/beat-copy", { genre: form.genre, bpm: form.bpm, musical_key: form.musical_key, tags: form.tags });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        tags: (data.tags || []).join(", ") || f.tags,
        price_mp3: data.prices?.mp3 ?? f.price_mp3,
        price_wav: data.prices?.wav ?? f.price_wav,
        price_exclusive: data.prices?.exclusive ?? f.price_exclusive,
      }));
      toast.success("AI draft generated — title, tags & market prices");
    } catch {
      toast.error("AI assistant unavailable right now");
    } finally {
      setAiLoading(false);
    }
  };

  const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks stay well under proxy limits on slow connections

  const uploadChunked = async (file, label) => {
    const { data: start } = await api.post("/admin/uploads/start", {
      filename: file.name, content_type: file.type || "application/octet-stream",
    });
    const uploadId = start.upload_id;
    const chunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    let done = new Set();
    const report = () => setUploadProgress(`${label} ${Math.round((done.size / chunks) * 100)}%`);

    // Up to 3 rounds: if the connection drops mid-upload, ask the server which
    // chunks it already has and resume from the gap instead of starting over.
    for (let round = 0; round < 3; round++) {
      try {
        if (round > 0) {
          const { data: st } = await api.get(`/admin/uploads/${uploadId}`);
          done = new Set(st.received_chunks);
          setUploadProgress(`${label} — reconnecting, resuming at ${Math.round((done.size / chunks) * 100)}%…`);
          await new Promise((r) => setTimeout(r, 1500));
        }
        for (let i = 0; i < chunks; i++) {
          if (done.has(i)) continue;
          const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          let sent = false;
          let lastErr = null;
          for (let attempt = 0; attempt < 3 && !sent; attempt++) {
            try {
              await api.post(`/admin/uploads/${uploadId}/chunk?index=${i}`, blob, {
                headers: { "Content-Type": "application/octet-stream" },
              });
              sent = true;
            } catch (err) {
              lastErr = err;
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
            }
          }
          if (!sent) throw lastErr;
          done.add(i);
          report();
        }
        const { data: fin } = await api.post(`/admin/uploads/${uploadId}/complete`, { chunks });
        return fin.path;
      } catch (err) {
        if (round === 2) throw err;
      }
    }
    throw new Error("Upload failed");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !audio) return toast.error("Upload an audio file for the beat.");
    setSaving(true);
    setUploadProgress("");
    const hasFiles = !!audio || !!(stems && stems.length) || !!cover;
    try {
      if (hasFiles) {
        // Chunked path: each file uploads in 10MB pieces so big WAVs and stem packs
        // can't blow past proxy body/time limits on a slow connection.
        let audioPath = "", audioName = "", audioCt = "";
        if (audio) {
          audioPath = await uploadChunked(audio, "Beat");
          audioName = audio.name;
          audioCt = audio.type || "audio/mpeg";
        }
        const stemPaths = [], stemNames = [];
        if (stems && stems.length) {
          for (const f of Array.from(stems)) {
            stemPaths.push(await uploadChunked(f, f.name));
            stemNames.push(f.name);
          }
        }
        let coverPath = "";
        if (cover) coverPath = await uploadChunked(cover, "Cover");
        setUploadProgress("Saving beat…");
        const payload = {
          ...form,
          bpm: parseInt(form.bpm, 10) || 0,
          price_mp3: parseFloat(form.price_mp3) || 0,
          price_wav: parseFloat(form.price_wav) || 0,
          price_exclusive: parseFloat(form.price_exclusive) || 0,
          audio_path: audioPath,
          audio_filename: audioName,
          audio_content_type: audioCt,
          stem_paths: stemPaths,
          stem_filenames: stemNames,
          cover_path: coverPath,
        };
        if (editing) {
          await api.put(`/admin/beats/${editing}/from-uploads`, payload);
          toast.success("Beat updated");
        } else {
          await api.post("/admin/beats/from-uploads", payload);
          toast.success("Beat uploaded");
        }
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (editing) {
          await api.put(`/admin/beats/${editing}`, fd);
          toast.success("Beat updated");
        } else {
          await api.post("/admin/beats", fd);
          toast.success("Beat uploaded");
        }
      }
      setForm(EMPTY_BEAT); setAudio(null); setCover(null); setStems(null); setEditing(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed — check your connection and try again");
    } finally {
      setSaving(false);
      setUploadProgress("");
    }
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({
      title: b.title, genre: b.genre, bpm: b.bpm, musical_key: b.musical_key || "",
      tags: (b.tags || []).join(", "),
      free_download: !!b.free_download,
      price_mp3: b.prices?.mp3 ?? 17.99, price_wav: b.prices?.wav ?? 27.99,
      price_exclusive: b.prices?.exclusive ?? 174.99,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <form onSubmit={submit} className="space-y-3 rounded-2xl border border-white/10 bg-[#141419] p-5 lg:col-span-2" data-testid="admin-beat-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-bold text-white">{editing ? "Edit Beat" : "Upload Beat"}</h3>
          <div className="flex gap-2">
            <button type="button" onClick={aiAssist} disabled={aiLoading} data-testid="admin-ai-assist-btn"
              className="flex items-center gap-1.5 rounded-full border border-emerald-300/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-200 hover:bg-emerald-300/10 disabled:opacity-50">
              {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Assist
            </button>
            <button type="button" disabled={tagChecking} data-testid="admin-tag-check-btn"
              onClick={async () => {
                setTagChecking(true);
                try {
                  const { data } = await api.post("/admin/ai/tag-check", { title: form.title, genre: form.genre, tags: form.tags });
                  if (!data.ai_classified) toast.info("Tag checker unavailable right now");
                  else if (data.consistent) toast.success(`Genre & tags look consistent (${Math.round((data.confidence || 0) * 100)}% sure)`);
                  else toast.warning(`Tags may not fit — suggested genre: ${data.suggested_genre}`);
                } catch { toast.error("Tag check failed"); } finally { setTagChecking(false); }
              }}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-300 hover:border-emerald-300/40 hover:text-emerald-200 disabled:opacity-50">
              {tagChecking ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Tag Check
            </button>
          </div>
        </div>
        <input required placeholder="Beat title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-beat-title-input" />
        <div className="grid grid-cols-3 gap-3">
          <select value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className={inputCls} data-testid="admin-beat-genre-select">
            {GENRES.map((g) => <option key={g}>{g}</option>)}
          </select>
          <input type="number" placeholder="BPM" value={form.bpm} onChange={(e) => setForm({ ...form, bpm: e.target.value })} className={inputCls} data-testid="admin-beat-bpm-input" />
          <input placeholder="Key" value={form.musical_key} onChange={(e) => setForm({ ...form, musical_key: e.target.value })} className={inputCls} data-testid="admin-beat-key-input" />
        </div>
        <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} data-testid="admin-beat-tags-input" />
        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 px-3 py-2.5" data-testid="admin-free-download-toggle">
          <input
            type="checkbox"
            checked={form.free_download}
            onChange={(e) => setForm({ ...form, free_download: e.target.checked })}
            className="h-4 w-4 accent-emerald-300"
            data-testid="admin-free-download-checkbox"
          />
          <span className="text-xs text-zinc-300">Free download — listed in the Free Beats section (artists unlock it with a follow/subscribe)</span>
        </label>
        <div className="grid grid-cols-3 gap-3">
          {[
            ["price_mp3", "MP3 Lease $", 14.99, 19.99],
            ["price_wav", "WAV+Stems $", 24.99, 29.99],
            ["price_exclusive", "Exclusive $", 149.99, 199.99],
          ].map(([k, label, min, max]) => (
            <label key={k} className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                {label} <span className="text-zinc-700">({min}–{max})</span>
              </span>
              <input type="number" step="0.01" value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls}
                data-testid={`admin-${k}-input`} />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-zinc-600">
          Set Exclusive to 0 to hide the Exclusive tier for this beat. AI Assist suggests market-based prices within these ranges.
        </p>
        {!editing && (
          <label className="block rounded-lg border border-dashed border-white/15 p-3 text-xs text-zinc-400" data-testid="admin-beat-audio-input">
            Audio file (MP3/WAV) *
            <input type="file" accept="audio/*" className="mt-2 block w-full text-xs" onChange={(e) => setAudio(e.target.files[0])} />
          </label>
        )}
        <label className="block rounded-lg border border-dashed border-white/15 p-3 text-xs text-zinc-400" data-testid="admin-beat-cover-input">
          Cover art (optional) — leave empty and AI paints a hip-hop cover from the beat's title, genre and BPM
          <input type="file" accept="image/*" className="mt-2 block w-full text-xs" onChange={(e) => setCover(e.target.files[0])} />
        </label>
        <label className="block rounded-lg border border-dashed border-emerald-300/25 p-3 text-xs text-zinc-400" data-testid="admin-beat-stems-input">
          Stems / trackouts for Premium buyers (optional) — pick one ZIP or multiple files, they get zipped automatically
          {editing && beats.find((b) => b.id === editing)?.stems_path && (
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-wider text-emerald-300">Stems already attached — uploading replaces them</span>
          )}
          <input type="file" multiple accept=".zip,.wav,.mp3,.aif,.aiff,.flac" className="mt-2 block w-full text-xs"
            onChange={(e) => setStems(e.target.files)} data-testid="admin-beat-stems-file" />
        </label>
        <div className="flex gap-2">
          <button type="submit" disabled={saving} data-testid="admin-upload-beat-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {saving && uploadProgress ? uploadProgress : editing ? "Save Changes" : "Upload Beat"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_BEAT); }} data-testid="admin-beat-cancel-edit"
              className="rounded-full border border-white/15 px-4 text-xs text-zinc-400">Cancel</button>
          )}
        </div>
      </form>

      <div className="space-y-3 lg:col-span-3" data-testid="admin-beats-list">
        {beats.map((b) => (
          <div key={b.id} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid={`admin-beat-row-${b.id}`}>
            <img src={fileUrl(b.cover_url)} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">
                {b.title}
                {b.free_download && (
                  <span className="ml-2 rounded-full bg-emerald-300/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-200" data-testid={`admin-beat-free-badge-${b.id}`}>Free</span>
                )}
                {b.stems_path && (
                  <span className="ml-2 rounded-full bg-sky-400/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-sky-300" data-testid={`admin-beat-stems-badge-${b.id}`}>Stems</span>
                )}
              </p>
              <p className="font-mono text-[11px] uppercase text-zinc-500">{b.genre} · {b.bpm} BPM · {b.musical_key}</p>
            </div>
            <button onClick={async () => {
              setAiCoverBusy(b.id);
              try {
                await api.post(`/admin/beats/${b.id}/ai-cover`);
                toast.success("New AI cover generated");
                load();
              } catch {
                toast.error("AI cover failed — try again");
              } finally {
                setAiCoverBusy(null);
              }
            }} disabled={aiCoverBusy === b.id} data-testid={`admin-beat-ai-cover-${b.id}`}
              title="Generate a new AI cover"
              className="rounded-full border border-emerald-300/30 p-2 text-emerald-300 hover:bg-emerald-300/10 disabled:opacity-50">
              {aiCoverBusy === b.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            </button>
            <button onClick={() => startEdit(b)} data-testid={`admin-beat-edit-${b.id}`}
              className="rounded-full border border-white/15 px-4 py-1.5 text-xs text-zinc-300 hover:border-emerald-300/50 hover:text-emerald-200">Edit</button>
            <button onClick={async () => { await api.delete(`/admin/beats/${b.id}`); load(); }} data-testid={`admin-beat-delete-${b.id}`}
              className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
          </div>
        ))}
        {beats.length === 0 && <p className="text-sm text-zinc-600">No beats yet — upload your first one.</p>}
      </div>
    </div>
  );
}

function GalleriesTab() {
  const [galleries, setGalleries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [photos, setPhotos] = useState([]); // {path, url, caption}
  const [progress, setProgress] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/galleries").then((r) => setGalleries(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const reset = () => { setEditing(null); setTitle(""); setCaption(""); setPhotos([]); setProgress(""); };

  const addPhotos = async (files) => {
    for (const f of Array.from(files)) {
      try {
        const path = await uploadChunkedFile(f, f.name, setProgress);
        setPhotos((ps) => [...ps, { path, url: `/api/files/${path}`, caption: "" }]);
      } catch {
        toast.error(`${f.name} failed to upload — try again`);
      }
    }
    setProgress("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!photos.length) return toast.error("Add at least one photo");
    setSaving(true);
    try {
      const payload = { title, caption, photos: photos.map((p) => ({ path: p.path, caption: p.caption })) };
      if (editing) {
        await api.put(`/admin/galleries/${editing}`, payload);
        toast.success("Gallery updated");
      } else {
        await api.post("/admin/galleries", payload);
        toast.success("Gallery published");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (g) => {
    setEditing(g.id);
    setTitle(g.title);
    setCaption(g.caption || "");
    setPhotos(g.photos.map((p) => ({ path: p.url.replace("/api/files/", ""), url: p.url, caption: p.caption || "" })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-6" data-testid="admin-galleries-tab">
      <form onSubmit={save} className="space-y-4 rounded-2xl border border-white/10 bg-[#141419] p-5" data-testid="admin-gallery-form">
        <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">
          {editing ? "Edit gallery" : "New gallery"}
        </p>
        <input
          required placeholder="Gallery title — e.g. 'Jay's cover art session'" value={title}
          onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="admin-gallery-title-input"
        />
        <textarea
          rows={2} placeholder="Caption for the set (optional)" value={caption}
          onChange={(e) => setCaption(e.target.value)} className={inputCls} data-testid="admin-gallery-caption-input"
        />
        <label className="block rounded-lg border border-dashed border-emerald-300/25 p-3 text-xs text-zinc-400" data-testid="admin-gallery-photos-drop">
          Add photos — pick several at once, they upload in safe chunks
          <input
            type="file" multiple accept="image/*" className="mt-2 block w-full text-xs"
            onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
            data-testid="admin-gallery-photos-input"
          />
        </label>
        {progress && (
          <p className="flex items-center gap-2 font-mono text-[11px] text-emerald-300" data-testid="admin-gallery-progress">
            <Loader2 size={12} className="animate-spin" /> {progress}
          </p>
        )}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="admin-gallery-photos-grid">
            {photos.map((p, i) => (
              <div key={p.path + i} className="relative rounded-xl border border-white/10 bg-[#0B0B0E] p-2" data-testid={`admin-gallery-photo-${i}`}>
                <img src={fileUrl(p.url)} alt="" className="aspect-square w-full rounded-lg object-cover" />
                <input
                  placeholder="Photo caption (optional)" value={p.caption}
                  onChange={(e) => setPhotos((ps) => ps.map((x, xi) => (xi === i ? { ...x, caption: e.target.value } : x)))}
                  className="mt-2 w-full rounded-md border border-white/10 bg-transparent px-2 py-1.5 text-[11px] text-white placeholder-zinc-600 outline-none focus:border-emerald-300/50"
                  data-testid={`admin-gallery-photo-caption-${i}`}
                />
                <button
                  type="button" onClick={() => setPhotos((ps) => ps.filter((_, xi) => xi !== i))}
                  data-testid={`admin-gallery-photo-remove-${i}`}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-1 text-white"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button type="submit" disabled={saving} data-testid="admin-gallery-save-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {editing ? "Save Gallery" : "Publish Gallery"}
          </button>
          {editing && (
            <button type="button" onClick={reset} data-testid="admin-gallery-cancel-btn"
              className="rounded-full border border-white/10 px-6 text-xs text-zinc-400 hover:text-white">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3" data-testid="admin-galleries-list">
        {galleries.length === 0 && (
          <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600" data-testid="admin-galleries-empty">
            No galleries yet — publish your first client set above.
          </p>
        )}
        {galleries.map((g) => (
          <div key={g.id} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid={`admin-gallery-row-${g.id.slice(0, 8)}`}>
            <img src={fileUrl(g.photos[0]?.url)} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{g.title}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{g.photos.length} photos</p>
            </div>
            <button onClick={() => startEdit(g)} data-testid={`admin-gallery-edit-${g.id.slice(0, 8)}`}
              className="rounded-full border border-white/15 px-4 py-1.5 text-xs text-zinc-300 hover:border-emerald-300/50 hover:text-emerald-200">
              Edit
            </button>
            <button
              onClick={async () => {
                if (!window.confirm(`Remove "${g.title}" from the site?`)) return;
                await api.delete(`/admin/galleries/${g.id}`);
                toast.success("Gallery removed");
                load();
              }}
              data-testid={`admin-gallery-delete-${g.id.slice(0, 8)}`}
              className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OffersTab() {
  const [offers, setOffers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [counterId, setCounterId] = useState(null);
  const [counterAmount, setCounterAmount] = useState("");
  const load = () => api.get("/admin/exclusive-offers").then((r) => setOffers(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const act = async (offer, action) => {
    setBusyId(offer.id);
    try {
      await api.post(`/admin/exclusive-offers/${offer.id}/${action}`);
      toast.success(`Offer ${action === "accept" ? "accepted — checkout link emailed to the artist" : "declined"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const sendCounter = async (offer) => {
    setBusyId(offer.id);
    try {
      await api.post(`/admin/exclusive-offers/${offer.id}/counter`, { counter_amount: parseFloat(counterAmount) });
      toast.success("Counter-offer sent — pay-now link emailed to the artist");
      setCounterId(null);
      setCounterAmount("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Counter failed");
    } finally {
      setBusyId(null);
    }
  };

  const statusCls = (s) =>
    s === "accepted" ? "bg-emerald-300/20 text-emerald-200" :
    s === "completed" ? "bg-emerald-300 text-black" :
    s === "countered" ? "bg-sky-400/15 text-sky-300" :
    s === "declined" ? "bg-red-500/15 text-red-400" : "bg-amber-400/15 text-amber-300";

  return (
    <div className="space-y-3" data-testid="admin-offers-list">
      {offers.length === 0 && (
        <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600" data-testid="admin-offers-empty">
          No exclusive offers yet. When an artist makes an offer on a beat, it lands here.
        </p>
      )}
      {offers.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid={`admin-offer-row-${o.id.slice(0, 8)}`}>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-white">
              "{o.beat_title}"
              <span className="ml-2 rounded-full bg-emerald-300/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-200" data-testid={`admin-offer-amount-${o.id.slice(0, 8)}`}>
                ${o.offer_amount?.toFixed(2)}
              </span>
              <span className={`ml-2 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${statusCls(o.status)}`} data-testid={`admin-offer-status-${o.id.slice(0, 8)}`}>
                {o.status}
              </span>
              {o.status === "countered" && o.counter_amount && (
                <span className="ml-2 rounded-full bg-sky-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-sky-300" data-testid={`admin-offer-counter-amount-${o.id.slice(0, 8)}`}>
                  Counter: ${o.counter_amount?.toFixed(2)}
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">{o.name} · {o.email}</p>
            {o.notes && <p className="mt-1 text-xs text-zinc-600">{o.notes}</p>}
            <p className="mt-1 font-mono text-[10px] uppercase text-zinc-700">{new Date(o.created_at).toLocaleString()}</p>
          </div>
          {o.status === "pending" && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {counterId === o.id ? (
                <>
                  <input
                    type="number" step="0.01" min="1" placeholder="Counter $"
                    value={counterAmount}
                    onChange={(e) => setCounterAmount(e.target.value)}
                    className="w-28 rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-xs text-white outline-none focus:border-sky-400/60"
                    data-testid={`admin-offer-counter-input-${o.id.slice(0, 8)}`}
                  />
                  <button onClick={() => sendCounter(o)} disabled={busyId === o.id || !counterAmount} data-testid={`admin-offer-counter-send-${o.id.slice(0, 8)}`}
                    className="flex items-center gap-1.5 rounded-full bg-sky-400 px-4 py-2 text-xs font-bold uppercase tracking-wider text-black hover:bg-sky-300 disabled:opacity-50">
                    {busyId === o.id ? <Loader2 size={12} className="animate-spin" /> : <DollarSign size={12} />} Send
                  </button>
                  <button onClick={() => { setCounterId(null); setCounterAmount(""); }} data-testid={`admin-offer-counter-cancel-${o.id.slice(0, 8)}`}
                    className="rounded-full border border-white/10 px-3 py-2 text-xs text-zinc-400 hover:text-white">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => act(o, "accept")} disabled={busyId === o.id} data-testid={`admin-offer-accept-${o.id.slice(0, 8)}`}
                    className="flex items-center gap-1.5 rounded-full bg-emerald-300 px-4 py-2 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50">
                    {busyId === o.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Accept
                  </button>
                  <button onClick={() => { setCounterId(o.id); setCounterAmount(""); }} disabled={busyId === o.id} data-testid={`admin-offer-counter-${o.id.slice(0, 8)}`}
                    className="flex items-center gap-1.5 rounded-full border border-sky-400/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-sky-300 hover:bg-sky-400/10 disabled:opacity-50">
                    <DollarSign size={12} /> Counter
                  </button>
                  <button onClick={() => act(o, "decline")} disabled={busyId === o.id} data-testid={`admin-offer-decline-${o.id.slice(0, 8)}`}
                    className="rounded-full border border-red-500/30 px-4 py-2 text-xs font-bold uppercase tracking-wider text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                    Decline
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DiscountsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", percent: 10, expires_at: "", max_uses: 0 });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/discounts").then((r) => setRows(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/discounts", form);
      setForm({ code: "", percent: 10, expires_at: "", max_uses: 0 });
      load();
      toast.success("Discount code created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create code");
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <form onSubmit={submit} className="space-y-3 rounded-2xl border border-white/10 bg-[#141419] p-5 lg:col-span-2" data-testid="admin-discount-form">
        <h3 className="font-heading flex items-center gap-2 font-bold text-white"><Tag size={15} className="text-emerald-300" /> New Discount Code</h3>
        <input
          required minLength={3} maxLength={24} placeholder="CODE (e.g. ZONE6)"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          className={`${inputCls} font-mono uppercase tracking-wider`}
          data-testid="admin-discount-code-input"
        />
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-zinc-500">Percent off (1–90)</span>
          <input type="number" min={1} max={90} value={form.percent}
            onChange={(e) => setForm({ ...form, percent: parseFloat(e.target.value) || 0 })}
            className={inputCls} data-testid="admin-discount-percent-input" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-zinc-500">Expires (optional)</span>
            <input type="date" min={today} value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className={`${inputCls} [color-scheme:dark]`} data-testid="admin-discount-expiry-input" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-zinc-500">Max uses (0 = ∞)</span>
            <input type="number" min={0} value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 0 })}
              className={inputCls} data-testid="admin-discount-maxuses-input" />
          </label>
        </div>
        <button type="submit" disabled={saving} data-testid="admin-discount-create-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50">
          {saving ? <Loader2 size={13} className="animate-spin" /> : "Create Code"}
        </button>
        <p className="text-[11px] leading-relaxed text-zinc-600">
          Live codes automatically show a gold sale banner across the store. Expired or maxed-out codes stop working on their own.
        </p>
      </form>

      <div className="space-y-3 lg:col-span-3" data-testid="admin-discounts-list">
        {rows.map((d) => (
          <div key={d.id} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid={`admin-discount-row-${d.code}`}>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-sm font-bold tracking-wider text-white">{d.code}</p>
              <p className="text-xs text-zinc-500">
                {d.percent}% off · used {d.uses || 0}{d.max_uses ? `/${d.max_uses}` : ""}×
                {d.expires_at && ` · ends ${d.expires_at}`}
              </p>
            </div>
            {!d.usable && d.active && (
              <span className="rounded-full bg-red-500/15 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-red-400" data-testid={`admin-discount-expired-${d.code}`}>
                {d.max_uses && (d.uses || 0) >= d.max_uses ? "Maxed out" : "Expired"}
              </span>
            )}
            <button
              onClick={async () => { await api.put(`/admin/discounts/${d.id}`, { active: !d.active }); load(); }}
              data-testid={`admin-discount-toggle-${d.code}`}
              className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${
                d.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/40 text-zinc-500"
              }`}>
              {d.active ? "Active" : "Off"}
            </button>
            <button onClick={async () => { await api.delete(`/admin/discounts/${d.id}`); load(); }} data-testid={`admin-discount-delete-${d.code}`}
              className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-zinc-600">No codes yet — create one to hand out to artists.</p>}
      </div>
    </div>
  );
}

function SimpleCrud({ endpoint, fields, testid, defaults }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(defaults);
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setRows(r.data.filter((x) => x.is_active !== false)));
  useEffect(() => { load().catch(() => {}); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    await api.post(`/admin/${endpoint}`, { ...form, sort: rows.length + 1 });
    setForm(defaults);
    load();
    toast.success("Saved");
  };

  return (
    <div className="grid gap-8 lg:grid-cols-5">
      <form onSubmit={submit} className="space-y-3 rounded-2xl border border-white/10 bg-[#141419] p-5 lg:col-span-2" data-testid={`admin-${testid}-form`}>
        {fields.map(([k, label, type]) =>
          type === "textarea" ? (
            <textarea key={k} rows={3} placeholder={label} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`admin-${testid}-${k}-input`} />
          ) : (
            <input key={k} placeholder={label} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`admin-${testid}-${k}-input`} />
          )
        )}
        <button type="submit" data-testid={`admin-${testid}-add-btn`}
          className="w-full rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200">Add</button>
      </form>
      <div className="space-y-3 lg:col-span-3">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid={`admin-${testid}-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{r.title || r.platform}</p>
              <p className="truncate text-xs text-zinc-500">{r.price_label || r.artist || r.url}</p>
            </div>
            <button onClick={async () => { await api.delete(`/admin/${endpoint}/${r.id}`); load(); }} data-testid={`admin-${testid}-delete-${r.id}`}
              className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-zinc-600">Nothing here yet.</p>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pendingOffers, setPendingOffers] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/session").then(() => setAuthed(true)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/admin/exclusive-offers/pending-count").then((r) => setPendingOffers(r.data.count || 0)).catch(() => {});
  }, [authed, tab]);

  if (!authed)
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-emerald-300" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Control room</p>
          <h1 className="font-heading mt-2 text-3xl font-extrabold text-white">Admin Dashboard</h1>
        </div>
        <button
          onClick={() => { localStorage.removeItem("admin_token"); navigate("/admin/login"); }}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-zinc-400 hover:text-white"
        >
          <LogOut size={13} /> Logout
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`relative rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
              tab === t ? "bg-emerald-300 text-black" : "border border-white/10 text-zinc-400 hover:text-white"
            }`}>
            {t}
            {t === "Offers" && pendingOffers > 0 && (
              <span data-testid="admin-offers-tab-badge"
                className={`absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 font-mono text-[10px] font-bold ${
                  tab === "Offers" ? "bg-red-500 text-white" : "bg-red-500 text-white shadow-[0_0_12px_rgba(239,68,68,0.6)]"
                }`}>
                {pendingOffers}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "Overview" && stats && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <Stat id="beats" label="Live Beats" value={stats.beats} />
            <Stat id="orders" label="Paid Orders" value={stats.orders} />
            <Stat id="revenue" label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
            <Stat id="bookings" label="Bookings" value={stats.bookings} />
            <Stat id="messages" label="Unread Msgs" value={stats.messages} />
          </div>
        )}

        {tab === "Beats" && <BeatsTab />}

        {tab === "Orders" && (
          <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-orders-table">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 bg-[#141419] font-mono text-[11px] uppercase tracking-wider text-zinc-500">
                <tr><th className="p-4">Item</th><th className="p-4">Type</th><th className="p-4">Amount</th><th className="p-4">Code</th><th className="p-4">Status</th><th className="p-4">Buyer</th><th className="p-4">Date</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.session_id} className="border-b border-white/5" data-testid={`admin-order-row-${o.session_id.slice(-8)}`}>
                    <td className="p-4 text-white">{o.beat_title || "Studio Deposit"}</td>
                    <td className="p-4 text-zinc-400">{o.type === "beat" ? o.tier_name : "Booking"}</td>
                    <td className="p-4 text-emerald-200">${((o.amount || 0) / 100).toFixed(2)}</td>
                    <td className="p-4 font-mono text-xs text-cyan-400">{o.discount_code || "—"}</td>
                    <td className="p-4">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${
                        o.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/40 text-zinc-400"
                      }`}>{o.payment_status}</span>
                    </td>
                    <td className="p-4 text-zinc-400">{o.buyer_email || "—"}</td>
                    <td className="p-4 text-zinc-500">{(o.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <p className="p-6 text-sm text-zinc-600">No orders yet.</p>}
          </div>
        )}

        {tab === "Bookings" && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            {(() => {
              let lastDate = null;
              return [...bookings]
                .sort((a, b) => `${a.date} ${a.time_slot}`.localeCompare(`${b.date} ${b.time_slot}`))
                .map((b) => {
                  const showHeader = b.date !== lastDate;
                  lastDate = b.date;
                  return (
                    <div key={b.id}>
                      {showHeader && (
                        <p className="pb-1 pt-4 font-mono text-xs uppercase tracking-widest text-emerald-300" data-testid={`booking-date-header-${b.date}`}>
                          {new Date(b.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#141419] p-4" data-testid={`admin-booking-row-${b.id.slice(0, 8)}`}>
                        <div className="w-20 shrink-0">
                          <p className="font-heading text-sm font-extrabold text-emerald-200" data-testid={`admin-booking-time-${b.id.slice(0, 8)}`}>{b.time_slot}</p>
                          <p className="font-mono text-[10px] uppercase text-zinc-600">1 hour</p>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-white">
                            {b.name}
                            <span className="ml-2 rounded-full bg-emerald-300/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-200">{b.service_type}</span>
                          </p>
                          <p className="text-xs text-zinc-500">{b.email} {b.phone && `· ${b.phone}`}</p>
                          {b.notes && <p className="mt-1 text-xs text-zinc-600">{b.notes}</p>}
                        </div>
                        <select
                          value={b.status}
                          onChange={async (e) => { await api.put(`/admin/bookings/${b.id}/status`, { status: e.target.value }); setBookings((bs) => bs.map((x) => x.id === b.id ? { ...x, status: e.target.value } : x)); }}
                          data-testid={`admin-booking-status-${b.id.slice(0, 8)}`}
                          className="rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-xs text-white"
                        >
                          {["pending", "confirmed", "completed", "cancelled"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                });
            })()}
            {bookings.length === 0 && <p className="text-sm text-zinc-600">No bookings yet.</p>}
          </div>
        )}

        {tab === "Offers" && <OffersTab />}

        {tab === "Discounts" && <DiscountsTab />}

        {tab === "Videos" && <VideosTab />}

        {tab === "Galleries" && <GalleriesTab />}

        {tab === "Videography" && <VideographyTab />}

        {tab === "Services" && <ServicesTab />}

        {tab === "Catalog" && (
          <SimpleCrud endpoint="catalog" testid="catalog"
            defaults={{ title: "", artist: "", role: "Producer", year: "", streams: "", cover_url: "", link: "" }}
            fields={[["title", "Record title"], ["artist", "Artist"], ["role", "Your role"], ["year", "Year"], ["streams", "Streams (e.g. 1.2M streams)"], ["cover_url", "Cover image URL"], ["link", "Listen link (optional)"]]} />
        )}

        {tab === "Socials" && (
          <SimpleCrud endpoint="socials" testid="social"
            defaults={{ platform: "", handle: "", url: "" }}
            fields={[["platform", "Platform (Instagram, YouTube…)"], ["handle", "Handle (@you)"], ["url", "Profile URL"]]} />
        )}

        {tab === "Messages" && (
          <div className="space-y-3" data-testid="admin-messages-list">
            {messages.map((m) => (
              <div key={m.id} className={`rounded-2xl border p-5 ${m.read ? "border-white/5 bg-[#101014]" : "border-emerald-300/30 bg-[#141419]"}`} data-testid={`admin-message-row-${m.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-white">
                    {m.name} <span className="font-normal text-zinc-500">· {m.email}</span>
                    {m.triage?.ai_classified && (
                      <span className="ml-2 inline-flex gap-1.5 align-middle">
                        <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                          m.triage.lead === "hot" ? "bg-emerald-300/20 text-emerald-200" :
                          m.triage.lead === "spam" || m.triage.spam ? "bg-red-500/15 text-red-400" : "bg-zinc-700/40 text-zinc-400"
                        }`} data-testid={`triage-lead-${m.id.slice(0, 8)}`}>{m.triage.spam ? "spam" : m.triage.lead}</span>
                        <span className="rounded-full bg-cyan-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-cyan-400" data-testid={`triage-intent-${m.id.slice(0, 8)}`}>{(m.triage.intent || "").replace("_", " ")}</span>
                      </span>
                    )}
                  </p>
                  {!m.read && (
                    <button onClick={async () => { await api.put(`/admin/contacts/${m.id}/read`); setMessages((ms) => ms.map((x) => x.id === m.id ? { ...x, read: true } : x)); }}
                      data-testid={`admin-message-read-${m.id.slice(0, 8)}`}
                      className="rounded-full border border-white/15 px-3 py-1 text-[11px] text-zinc-400 hover:text-emerald-200">Mark read</button>
                  )}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-400">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && <p className="text-sm text-zinc-600">No messages yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
